import React, { useState } from 'react';
import { Controller, Keyboard, Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperClass, SwiperSlide } from 'swiper/react';

import { ButtonGroup } from '@/components/shared/element/buttons/button-group';
import { Icon } from '@/components/shared/element/icons';
import { Link } from '@/components/shared/element/link';
import { Text } from '@/components/shared/element/text';
import { Box } from '@/components/shared/layout/box';
import { Section } from '@/components/shared/layout/section';
import { Image } from '@/components/shared/media/image';
import { generateCommonImageSources } from '@/components/shared/media/image/generate-sources';
import { ContentWithAction2 } from '@/components/shared/my24/content-with-action-2';
import {
  CursorButton,
  HOVER_CLASS,
} from '@/components/shared/my24/cursor-button';
import { LineupMenuSlider } from '@/components/shared/my24/lineup-menu-slider';
import { styled } from '@/stitches.config';
import { IHeadline, IMy24LineupItem, IMy24Tracking } from '@/types/shared';

const LineupBg = styled(Box, {
  marginTop: 'clamp(195px, calc(195 / 360 * 100%), 320px)',
  mx: 'auto',
  width: `${(328 / 360) * 100 + '%'}`,
  aspectRatio: '16 / 9',
  pe: 'none',
  '@lg': {
    marginTop: `${(150 / 1024) * 100 + '%'}`,
    width: `${(718 / 1024) * 100 + '%'}`,
    maxWidth: 864,
  },
  '@2xl': {
    marginTop: 150,
    width: '100%',
  },
});

const LineupBgWrap = styled(Box, {
  minHeight: 320,
  height: `${(320 / 184.5) * 100 + '%'}`,
  bottom: `${(60 / 184.5) * 100 + '%'}`,
  left: '50%',
  transform: 'translateX(-50%)',
  overflow: 'hidden',
  '@lg': {
    height: `${(454 / 404) * 100 + '%'}`,
    bottom: `${(183 / 486) * 100 + '%'}`,
  },
  '@2xl': {
    height: 454,
    bottom: 183,
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    right: 0,
    width: '100%',
    height: '100%',
    linearGradient: '180deg, rgba(255, 255, 255, 0) 60%, $white 100%',
    zIndex: 1,
    pe: 'none',
  },
});

const LineupBgItem = styled(Box, {
  opacity: 0,
  transition: 'opacity 1s ease-in-out',
  variants: {
    fade: {
      true: {
        opacity: 1,
      },
    },
  },
});

const LineupMenu = styled(Box, {
  top: `clamp(56px,  ${(56 / 360) * 100 + 'vw'}, 80px)`,
  zIndex: 10,
  '@lg': {
    top: 64,
  },
});

const LineupModelImage = styled(Box, {
  width: '100%',
  mx: 'auto',
  '@2xl': {
    maxWidth: 864,
  },
});

const LineupModelInner = styled(Box, {
  position: 'relative',
  width: `${(328 / 262) * 100 + '%'}`,
  mx: 'auto',
  left: '50%',
  transform: 'translateX(-50%)',
  '@lg': {
    width: `${(718 / 617) * 100 + '%'}`,
    maxWidth: 864,
  },
  '@2xl': {
    width: '100%',
    maxWidth: 864,
    left: 'auto',
    transform: 'none',
  },
});

const LineupModel = styled(Box, {
  marginTop: 'clamp(195px, calc(195 / 360 * 100%), 320px)',
  mx: 'auto',
  width: `${(262 / 360) * 100 + '%'}`,
  '@lg': {
    marginTop: `${(150 / 1024) * 100 + '%'}`,
    width: `${(617 / 1024) * 100 + '%'}`,
  },
  '@2xl': {
    marginTop: 150,
    width: 864,
  },
  '& .swiper': {
    overflow: 'initial',
    width: '100%',
  },
  '& .swiper-slide': {
    width: '100%',
    [`& ${LineupModelImage}`]: {
      transform: `scale(0.85) translateZ(0)`,
      transformOrigin: 'center center',
      transitionProperty: 'transform',
      transitionDuration: '1s',
      transitionTimingFunction: 'ease-in-out',
      willChange: 'transform',
      '@lg': {
        transform: `scale(0.8) translateZ(0)`,
      },
    },
    [`& .lineup-content`]: {
      position: 'relative',
      opacity: 0,
      transitionProperty: 'opacity, transform',
      transitionDuration: '0.4s, 0.4s',
      transitionTimingFunction: 'ease-in-out',
      willChange: 'opacity, transform',
    },
    [`& ${ButtonGroup}`]: {
      position: 'relative',
      pe: 'none',
      '& a, & button': {
        '@maxsm': {
          maxWidth: 'calc(100vw - 48px)',
        },
      },
    },
    '&.swiper-slide-active': {
      [`& ${LineupModelImage}`]: {
        transform: 'scale(1) translateZ(0)',
      },
      [`& .lineup-content`]: {
        opacity: 1,
        transitionDuration: '1s, 1s',
      },
      [`& ${ButtonGroup}`]: {
        pe: 'all',
      },
    },
  },
  '& .swiper-slide-prev, & .swiper-slide-next': {
    pe: 'none',
  },
});

const LineupHover = styled(Box, {
  '@lg': {
    bc: 'rgba(0, 0, 0, 0)',
    width: 'calc((100% - (718 / 1024 * 100%)) / 2)',
  },
  '@2xl': {
    width: 'calc((100% - 864px) / 2)',
  },
  '@maxlg': {
    display: 'none',
  },
  '&.swiper-button-disabled': {
    pe: 'none',
    opacity: '0',
    visibility: 'hidden',
  },
  '& > *': {
    display: 'none',
  },
});

export interface IMY24LineupCarousel {
  active?: boolean;
  headline: IHeadline;
  items?: IMy24LineupItem[];
  dataTrack?: IMy24Tracking;
}

const LCVHomeLineup = ({
  active,
  headline: { title },
  items,
  dataTrack = 'lcv-home',
}: IMY24LineupCarousel) => {
  const [indexSlide, setIndexSlide] = useState<number>(0);
  const [menuSwiperSlide, setMenuSwiperSlide] = useState<SwiperClass | null>(
    null
  );
  const [modelSwiperSlide, setModelSwiperSlide] = useState<SwiperClass | null>(
    null
  );

  const onClickMenu = (index: number) => {
    modelSwiperSlide.loopedSlides
      ? modelSwiperSlide.slideToLoop(index)
      : modelSwiperSlide.slideTo(index);
  };

  const onModelSlideChange = (index: number) => {
    setIndexSlide(index);
  };

  return (
    <>
      {!!active && items?.length > 0 && (
        <Section
          size="2xl"
          test="section_lineup"
          data-impression="lineup-carousel"
          css={{ textAlign: 'center', paddingTop: '$space-0', zIndex: 2 }}
        >
          {!!title && (
            <Text as="h2" size="h2" sr-only>
              {title}
            </Text>
          )}
          <LineupBg position="absolute" left="0" right="0">
            <LineupBgWrap
              position="absolute"
              width="w-screen"
              test={{
                dataTest: 'lineup_background',
              }}
            >
              {items?.map((item, i) => (
                <LineupBgItem
                  key={i}
                  position="absolute"
                  left="0"
                  top="0"
                  width="full"
                  height="full"
                  fade={indexSlide === i}
                  test={{
                    dataTest: `lineup_background_item_${i + 1}`,
                  }}
                >
                  <Image
                    rootMargin="0px 0px -1px 0px"
                    src={item?.bg?.src}
                    alt={item?.bg?.alt}
                    width={1600}
                    height={900}
                    layout="fill"
                    sources={generateCommonImageSources({
                      mobileSrc: item?.bg?.src,
                      mobileOptions: {
                        crop: '9:8',
                      },
                      desktopSrc: item?.bgDesktop?.src,
                      desktopOptions: {
                        crop: '3:1',
                      },
                    })}
                  />
                </LineupBgItem>
              ))}
            </LineupBgWrap>
          </LineupBg>
          <LineupMenu position="absolute" left="0" width="full">
            <LineupMenuSlider
              items={items}
              dataTest="lineup"
              dataTrack={dataTrack}
              slideIndex={indexSlide}
              onSwiper={setMenuSwiperSlide}
              control={modelSwiperSlide}
              onClickText={onClickMenu}
              theme="dark"
              shadow
            />
          </LineupMenu>
          <LineupModel position="relative">
            <Swiper
              modules={[Pagination, Navigation, Keyboard, Controller]}
              keyboard
              speed={2000}
              threshold={10}
              slidesPerView={'auto'}
              spaceBetween={0}
              centeredSlides
              centeredSlidesBounds
              navigation={{
                prevEl: '.button-home-lineup-prev',
                nextEl: '.button-home-lineup-next',
              }}
              breakpoints={{
                '1440': {
                  spaceBetween: -24,
                },
              }}
              onSwiper={setModelSwiperSlide}
              onSlideChange={(e) => onModelSlideChange(e.realIndex)}
              controller={{ control: menuSwiperSlide }}
              data-test={`lineup_items`}
            >
              {items?.map((item, i) => (
                <SwiperSlide key={i} data-test={`lineup_item_${i + 1}`}>
                  <LineupModelInner>
                    {!!item?.image?.src && (
                      <LineupModelImage position="relative">
                        <Image
                          rootMargin="10% 0px 0px 0px"
                          src={item?.image?.src}
                          alt={item?.image?.alt}
                          width={1600}
                          height={900}
                          layout="responsive"
                          aspectRatio="16by9"
                          srcSetOptions={{ crop: '16:9' }}
                          sizes="(max-width: 1023px) 100vw, 960px"
                          useSrcSetPattern={true}
                        />
                        <Link
                          key={i}
                          title={item?.title}
                          href={item?.button?.url}
                          target={item?.button?.target}
                          clickable
                          tracking={{
                            dataTrack: dataTrack,
                            dataTrackSection: 'lineup',
                            dataTrackText: item?.button?.label,
                            dataTrackValue: item?.preTitle,
                            dataTrackUrl: item?.button?.url,
                          }}
                          test={{
                            dataTest: `lineup_item_ctaLink`,
                          }}
                        >
                          {item?.title}
                        </Link>
                      </LineupModelImage>
                    )}
                    <Box
                      css={{
                        marginTop: '$space-6',
                        px: '$space-2',
                        '@xs': {
                          px: '$space-0',
                        },
                        '@sm': {
                          marginTop: '$space-8',
                        },
                        '@md': {
                          marginTop: '$space-10',
                        },
                        '@lg': {
                          marginTop: 0,
                        },
                      }}
                    >
                      <ContentWithAction2
                        item={item}
                        dataTest="lineup"
                        dataTrack={dataTrack}
                        className="lineup-content"
                      />
                    </Box>
                  </LineupModelInner>
                </SwiperSlide>
              ))}
            </Swiper>
          </LineupModel>
          {items?.length > 1 && (
            <>
              <LineupHover
                position="absolute"
                left="0"
                top="0"
                height="full"
                zIndex="1"
                className={`${HOVER_CLASS} button-home-lineup-prev`}
                tracking={{
                  dataTrack: dataTrack,
                  dataTrackSection: 'lineup',
                  dataTrackText: 'Previous',
                  dataTrackValue:
                    items[indexSlide - 1] &&
                    `${items[indexSlide - 1]?.preTitle}`,
                }}
                test={{ dataTest: 'lineup_previousButton' }}
              >
                <Icon icon="arrow-back" color="icon-white" />
              </LineupHover>
              <LineupHover
                position="absolute"
                top="0"
                height="full"
                right="0"
                zIndex="1"
                className={`${HOVER_CLASS} button-home-lineup-next`}
                tracking={{
                  dataTrack: dataTrack,
                  dataTrackSection: 'lineup',
                  dataTrackText: 'Next',
                  dataTrackValue:
                    items[indexSlide + 1] &&
                    `${items[indexSlide + 1]?.preTitle}`,
                }}
                test={{ dataTest: 'lineup_nextButton' }}
              >
                <Icon icon="arrow-forward" color="icon-white" />
              </LineupHover>
            </>
          )}
          {items?.length > 1 && <CursorButton />}
        </Section>
      )}
    </>
  );
};

LCVHomeLineup.displayName = 'SectionMY24HomeLineUp';
export default LCVHomeLineup;
